"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { ArrowLeft, Calendar, FileText, Mail, MapPin, Shield } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
export default function Disclosure() {
  const shouldReduceMotion = useReducedMotion();
  const rightsList = ["Kişisel verilerinizin işlenip işlenmediğini öğrenme,", "Kişisel verileriniz işlenmişse buna ilişkin bilgi talep etme,", "İşlenme amacını ve bu amaca uygun kullanılıp kullanılmadığını öğrenme,", "Verilerin aktarıldığı üçüncü kişileri bilme,", "Eksik veya yanlış işlenen verilerin düzeltilmesini isteme,", "Kanunda öngörülen şartlar çerçevesinde verilerin silinmesini veya yok edilmesini isteme,", "Düzeltme, silme veya yok etme işlemlerinin verilerin aktarıldığı üçüncü kişilere bildirilmesini isteme,", "Münhasıran otomatik sistemlerle yapılan analiz sonucunda aleyhe bir sonucun ortaya çıkmasına itiraz etme,", "Kanuna aykırı veri işleme nedeniyle zarara uğranılması halinde zararın giderilmesini talep etme."];
  return <section id="contactformlegaldisclosure" data-nav="light" className="bg-background text-foreground min-h-screen py-16 md:py-24 px-4 sm:px-6 lg:px-8 font-serif">
      <div className="max-w-4xl mx-auto">
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="space-y-8">
          {}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-border/40">
            <Button variant="ghost" size="sm" asChild className="self-start font-sans text-xs tracking-wide">
              <Link to="/ContactPage" className="inline-flex items-center gap-2">
                <ArrowLeft className="size-4" />
                İletişim Sayfasına Dön
              </Link>
            </Button>

            <div className="flex items-center gap-3">
              <Badge variant="outline" className="font-mono text-xs border-border/60 text-muted-foreground">
                <FileText className="size-3" />
                Sürüm: 22.08.2026 / v1.0
              </Badge>
              <Badge variant="outline" className="font-mono text-xs border-border/60 text-muted-foreground">
                <SafeHydrate><Calendar className="size-3" /></SafeHydrate>
                Son Güncelleme: 22 Ağustos 2026
              </Badge>
            </div>
          </div>

          {}
          <header className="space-y-4 pt-2">
            <div className="inline-flex items-center gap-2 text-xs font-mono uppercase text-muted-foreground tracking-widest">
              <Shield className="size-4 text-primary" />
              6698 Sayılı KVKK Uyarınca
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-default font-normal tracking-tight leading-tight">
              İletişim Formu Aydınlatma Metni
            </h1>
            <p className="font-sans text-sm sm:text-base text-muted-foreground leading-relaxed max-w-3xl">
              Bu aydınlatma metni, internet sitemizde yer alan iletişim formu aracılığıyla toplanan kişisel verilerinizin işlenmesine ilişkin şart ve ilkeleri açıklamaktadır.
            </p>
          </header>

          <Separator className="bg-border/60 my-8" />

          {}
          <article className="space-y-12 text-foreground font-sans">
            {}
            <section className="space-y-4">
              <h2 className="text-xl sm:text-2xl font-serif text-foreground font-semibold tracking-wide">
                1. Veri Sorumlusu
              </h2>
              <p className="text-sm sm:text-base leading-relaxed text-muted-foreground">
                6698 sayılı Kişisel Verilerin Korunması Kanunu (&ldquo;KVKK&rdquo;) kapsamında veri sorumlusu:
              </p>
              <div className="p-5 rounded-md bg-card border border-border/50 text-card-foreground space-y-3">
                <p className="font-semibold text-foreground">
                  Av. Raci Çetin Yüksekbaş / Yüksekbaş Hukuk ve Danışmanlık
                </p>
                <div className="flex items-start gap-2 text-xs sm:text-sm text-muted-foreground">
                  <MapPin className="size-4 shrink-0 mt-0.5 text-primary" />
                  <span>Cevizli Mahallesi, Mustafa Kemal Caddesi, Onat Sokak, No:5/8, Kartal/İstanbul</span>
                </div>
                <div className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground">
                  <Mail className="size-4 shrink-0 text-primary" />
                  <span>
                    İletişim e-postası:{" "}
                    <a href="mailto:info@yuksekbas.av.tr" className="text-foreground underline underline-offset-4 hover:text-muted-foreground transition-colors">
                      info@yuksekbas.av.tr
                    </a>
                  </span>
                </div>
              </div>
            </section>

            {}
            <section className="space-y-4">
              <h2 className="text-xl sm:text-2xl font-serif text-foreground font-semibold tracking-wide">
                2. İşlenen Kişisel Veriler
              </h2>
              <p className="text-sm sm:text-base leading-relaxed text-muted-foreground">
                İletişim formunun kullanılması halinde aşağıdaki kişisel veriler işlenebilir:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-sm sm:text-base text-muted-foreground">
                <li><strong className="text-foreground">Kimlik bilgisi:</strong> ad ve soyad,</li>
                <li><strong className="text-foreground">İletişim bilgileri:</strong> e-posta adresi ve telefon numarası,</li>
                <li><strong className="text-foreground">İletişim içeriği:</strong> kullanıcının mesaj alanına yazdığı bilgiler,</li>
                <li>Gerekli olması halinde formun güvenliği ve kötüye kullanımın önlenmesi amacıyla sınırlı teknik kayıtlar.</li>
              </ul>
              <div className="p-4 rounded-md bg-muted/40 border border-border/40 text-xs sm:text-sm text-muted-foreground">
                İletişim formu aracılığıyla özel nitelikli kişisel veri talep edilmemektedir. Hukuki talebiniz bakımından gerekli olmadıkça özel nitelikli kişisel verileri veya üçüncü kişilere ait kişisel verileri form üzerinden paylaşmamanız önerilir.
              </div>
            </section>

            {}
            <section className="space-y-4">
              <h2 className="text-xl sm:text-2xl font-serif text-foreground font-semibold tracking-wide">
                3. Kişisel Verilerin İşlenme Amaçları
              </h2>
              <p className="text-sm sm:text-base leading-relaxed text-muted-foreground">
                Kişisel verileriniz;
              </p>
              <ul className="list-disc pl-6 space-y-2 text-sm sm:text-base text-muted-foreground">
                <li>İletişim talebinizin alınması,</li>
                <li>Talebin incelenmesi ve yanıtlanması,</li>
                <li>Gerektiğinde sizinle iletişim kurulması,</li>
                <li>İletişim ve randevu talepleri kapsamında telefon yoluyla geri dönüş sağlanması ve randevu sürecinin koordine edilmesi,</li>
                <li>Talebin niteliğine göre olası bir hukuki hizmet ilişkisinin kurulmasının değerlendirilmesi,</li>
                <li>Bir hakkın tesisi, kullanılması veya korunmasının gerekli olduğu durumlarda ilgili işlemlerin yürütülmesi,</li>
                <li>Bilgi ve sistem güvenliğinin sağlanması ile kötüye kullanımın önlenmesi</li>
              </ul>
              <p className="text-sm sm:text-base leading-relaxed text-muted-foreground pt-1">
                amaçlarıyla sınırlı olarak işlenir. Verileriniz reklam, doğrudan pazarlama veya ticari elektronik ileti gönderimi amacıyla kullanılmaz.
              </p>
            </section>

            {}
            <section className="space-y-4">
              <h2 className="text-xl sm:text-2xl font-serif text-foreground font-semibold tracking-wide">
                4. Kişisel Verilerin İşlenmesinin Hukuki Sebebi
              </h2>
              <p className="text-sm sm:text-base leading-relaxed text-muted-foreground">
                Kişisel verileriniz, somut işlemin niteliğine göre KVKK&rsquo;nın 5 inci maddesinde düzenlenen;
              </p>
              <ul className="list-disc pl-6 space-y-2 text-sm sm:text-base text-muted-foreground">
                <li>Bir sözleşmenin kurulması veya ifasıyla doğrudan doğruya ilgili olması,</li>
                <li>Bir hakkın tesisi, kullanılması veya korunması için veri işlemenin zorunlu olması,</li>
                <li>İlgili kişinin temel hak ve özgürlüklerine zarar vermemek kaydıyla veri sorumlusunun meşru menfaatleri için veri işlemenin zorunlu olması</li>
              </ul>
              <p className="text-sm sm:text-base leading-relaxed text-muted-foreground">
                hukuki sebeplerine dayanılarak işlenebilir.
              </p>
              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                Özel nitelikli bir kişisel verinin kullanıcı tarafından kendiliğinden paylaşılması halinde söz konusu veri yalnız KVKK&rsquo;nın 6 ncı maddesinde öngörülen işleme şartlarından birinin bulunması ve işlenmesinin gerekli olması ölçüsünde işlenir; gerekli olmayan veriler bakımından veri minimizasyonu ilkesi uygulanır.
              </p>
            </section>

            {}
            <section className="space-y-4">
              <h2 className="text-xl sm:text-2xl font-serif text-foreground font-semibold tracking-wide">
                5. Kişisel Verilerin Toplanma Yöntemi
              </h2>
              <p className="text-sm sm:text-base leading-relaxed text-muted-foreground">
                Kişisel verileriniz, internet sitesindeki iletişim formunun tarafınızca doldurulması ve gönderilmesi suretiyle elektronik ortamda elde edilir.
              </p>
              <p className="text-sm sm:text-base leading-relaxed text-muted-foreground">
                Form içeriği:{" "}
                <a href="mailto:iletisimegec@yuksekbas.av.tr" className="text-foreground font-medium underline underline-offset-4 hover:text-muted-foreground transition-colors">
                  iletisimegec@yuksekbas.av.tr
                </a>{" "}
                adresine iletilir.
              </p>
            </section>

            {}
            <section className="space-y-4">
              <h2 className="text-xl sm:text-2xl font-serif text-foreground font-semibold tracking-wide">
                6. Kişisel Verilerin Aktarılması
              </h2>
              <p className="text-sm sm:text-base leading-relaxed text-muted-foreground">
                Kişisel verileriniz, yalnızca işleme amacıyla sınırlı ve gerekli olduğu ölçüde;
              </p>
              <ul className="list-disc pl-6 space-y-2 text-sm sm:text-base text-muted-foreground">
                <li>Yüksekbaş Hukuk ve Danışmanlık bünyesinde erişimi gereken kişiler,</li>
                <li>İnternet sitesi, barındırma ve e-posta altyapısının çalışması için zorunlu teknik hizmet sağlayıcılar,</li>
                <li>Hukuken yetkili kamu kurumları, mahkemeler ve diğer merciler</li>
              </ul>
              <p className="text-sm sm:text-base leading-relaxed text-muted-foreground">
                ile paylaşılabilir. Teknik hizmet sağlayıcılar üzerinden yurt dışına veri aktarımı meydana gelmesi halinde KVKK&rsquo;nın 9 uncu maddesinde öngörülen şartlara uygun hareket edilir.
              </p>
            </section>

            {}
            <section className="space-y-4">
              <h2 className="text-xl sm:text-2xl font-serif text-foreground font-semibold tracking-wide">
                7. Saklama Süresi
              </h2>
              <p className="text-sm sm:text-base leading-relaxed text-muted-foreground">
                İletişim talebinin bir avukatlık veya hukuki hizmet ilişkisine dönüşmemesi halinde form aracılığıyla alınan veriler, talebin sonuçlanmasından itibaren <strong className="text-foreground font-medium">en fazla bir yıl</strong> süreyle muhafaza edilir ve sonrasında hukuki veya teknik bir saklama zorunluluğu bulunmadığı ölçüde silinir, yok edilir veya anonim hâle getirilir.
              </p>
              <p className="text-sm sm:text-base leading-relaxed text-muted-foreground">
                Bir avukatlık/hukuki hizmet ilişkisinin kurulması veya hukuki bir hakkın tesisi, kullanılması ya da korunması bakımından verinin saklanmasının gerekli olması halinde ilgili veriler, bu ilişkinin ve uygulanabilir yasal saklama veya zamanaşımı sürelerinin gerektirdiği süre boyunca muhafaza edilebilir.
              </p>
            </section>

            {}
            <section className="space-y-4">
              <h2 className="text-xl sm:text-2xl font-serif text-foreground font-semibold tracking-wide">
                8. KVKK Kapsamındaki Haklarınız
              </h2>
              <p className="text-sm sm:text-base leading-relaxed text-muted-foreground">
                KVKK&rsquo;nın 11 inci maddesi kapsamında ilgili kişiler;
              </p>
              <ul className="list-disc pl-6 space-y-2 text-sm sm:text-base text-muted-foreground">
                {rightsList.map((right, i) => <li key={i} data-index={i}>
                    {right}
                  </li>)}
              </ul>
              <p className="text-sm sm:text-base leading-relaxed text-muted-foreground pt-2">
                KVKK kapsamındaki başvurular, KVKK&rsquo;nın 13 üncü maddesi ve Veri Sorumlusuna Başvuru Usul ve Esasları Hakkında Tebliğ&rsquo;de belirtilen yöntemlerle veri sorumlusuna iletilebilir.
              </p>
            </section>
          </article>

          {}
          <div className="pt-8 mt-12 border-t border-border/40 flex flex-col sm:flex-row items-center justify-between gap-4 font-sans text-xs text-muted-foreground">
            <p>
              <strong>Son güncelleme:</strong> 22 Ağustos 2026
            </p>
            <div className="flex gap-4">
              <Button variant="outline" size="sm" asChild className="text-xs">
                <Link to="/ContactPage">İletişime Geç</Link>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}